import type {LocalPresentation} from './local-presentation-cache';

type SnapshotTrack = {
  id: string; sourceType: string; fingerprint?: string; size?: number;
  lyrics?: unknown[]; amllLyrics?: unknown[]; metadata?: Record<string, unknown>;
};
type Snapshot = {tracks: SnapshotTrack[]; lyrics: Record<string, unknown>};
const sameLines = (value: unknown[] | undefined, cached: unknown[]) => value === cached || (!value?.length && !cached.length);

/** Omit only presentation data whose exact value is already saved in IndexedDB.
 * The caller's map contains successful saves/readbacks, never pending writes.
 * New manual/sidecar arrays stay in the index even if their text looks equal.
 */
export function compactLocalPresentationSnapshot<T extends Snapshot>(state: T, persisted: ReadonlyMap<string, LocalPresentation>): T {
  let changed = false, lyrics = state.lyrics;
  const tracks = state.tracks.map(track => {
    const cache = persisted.get(track.id);
    if (track.sourceType !== 'local' || !track.fingerprint || !cache || cache.id !== track.id
      || cache.fingerprint !== track.fingerprint || cache.size !== track.size) return track;
    let next = track;
    if (track.metadata?.localIndexLyricsOverride !== true && sameLines(track.lyrics, cache.lyrics.lyrics) && sameLines(track.amllLyrics, cache.lyrics.amllLyrics)
      && (Object.hasOwn(track, 'lyrics') || Object.hasOwn(track, 'amllLyrics'))) {
      const {lyrics: _lines, amllLyrics: _amll, ...index} = track;
      next = index;
    }
    if (track.metadata?.localIndexLyricsOverride !== true && Object.hasOwn(lyrics, track.id) && lyrics[track.id] === cache.lyrics.lyrics) {
      if (lyrics === state.lyrics) lyrics = {...lyrics};
      delete lyrics[track.id]; changed = true;
    }
    if (track.metadata) {
      let metadata = track.metadata;
      const texts = {embeddedPlainLyrics: cache.lyrics.plainText, embeddedTranslation: cache.lyrics.translationText, embeddedRoman: cache.lyrics.romanText};
      for (const [key, text] of Object.entries(texts)) if (typeof text === 'string' && metadata[key] === text) {
        if (metadata === track.metadata) metadata = {...metadata};
        delete metadata[key];
      }
      if (metadata !== track.metadata) next = {...next, metadata};
    }
    if (next !== track) changed = true;
    return next;
  });
  return changed ? {...state, tracks, lyrics} as T : state;
}
